import JSON5 from 'json5';
import { scrollToElem, scrollToFirstError } from 'commons/resources/utils/scroll';
import { saveRegistrationModel, finalizeUserRegistration, finalizeAddContact } from 'UserRegistration/resources/api/saveRegistration';
import { getMpgKey, createMpgCreditCard, callManualMpgRedirect, registerCreateFailEvent } from 'UserRegistration/resources/api/mpg';
import { buildErrorArray } from './apiErrorsUtils';
import { doesRedirectToConfirmationPage } from './UserRegistrationStatus';
import { pushRegistrationConfirmationVirtualPageView, pushRegistrationECommerceData } from './analytics';

/**
 * Retrieves errors from forms of all of the configured sections. This is a 
 * useful method that can be used to determine if forms from any section currently 
 * contain any errors.
 * 
 * @param {object} componentConfig 
 * @param {object} registrationData 
 */
export function getRegistrationFormErrors(componentConfig, registrationData) {

    const errors = [];

    componentConfig.activeComponents.forEach((componentName) => {

        const componentDef = componentConfig.componentDefinition[componentName];

        if (componentDef.props.formikRef && componentDef.sequence <= registrationData.metadata.currentStep) {
            if (Object.keys(componentDef.props.formikRef.current.errors).length) {
                errors.push(componentDef.props.formikRef.current.errors);
            }
        }

    });

    // If there were errors, we need to make sure all forms are "not submitting" any longer.
    if (errors.length) {
        resetApplicableComponentsSubmittingState(componentConfig);
    }

    return errors;

}

/**
 * Saves the registration data entered by the user. If the user has reached the final step of the 
 * registration, the method will save and finalize the registration. Otherwise, a simple save of the 
 * data will be performed.
 * 
 * @param {object} componentConfig 
 * @param {object} registrationData 
 * @param {function} setRegistrationData
 * @param {function} saveSuccessCallback
 */
export function saveSection(componentConfig, registrationData, setRegistrationData, saveSuccessCallback) {
    
    let registrationDataCopy = Object.assign({}, registrationData);

    // Clear errors at the start of the save. (Needed for the animation to show up)
    registrationDataCopy.validationErrors = [];
    setRegistrationData(registrationDataCopy);

    const errors = getRegistrationFormErrors(componentConfig, registrationData);

    // If there are no errors, save the section
    if (!errors.length) {
        // If we are on the last active step, finalize the registration.
        if (registrationData.metadata.currentStep >= componentConfig.activeComponents.length) {

            // Update the global "isSubmitting" flag.
            registrationDataCopy = Object.assign({}, registrationDataCopy);
            registrationDataCopy.metadata.isSubmitting = true;
            setRegistrationData(registrationDataCopy); 

            // Begin the registration finalization.
            finalizeRegistration(componentConfig, registrationDataCopy, setRegistrationData, saveSuccessCallback);

        } else {
            saveRegistration(componentConfig, registrationDataCopy, setRegistrationData, saveSuccessCallback);
        }

    } else {
        scrollToFirstError(errors[0]);
    }

}

/**
 * Reset the submitting status of the current context's applicable components within the application so that the form will be
 * allowed to resubmit after displaying errors.
 * 
 * @param {object} componentConfig 
 */
const resetApplicableComponentsSubmittingState = (componentConfig) => {

    // Set all Forms' isSubmitting to false.
    componentConfig.applicableComponents.forEach((componentName) => {

        const componentDef = componentConfig.componentDefinition[componentName];

        // Set all Forms' isSubmitting to false.
        componentDef.props.formikRef?.current?.setSubmitting(false);

    });

}

/**
 * Saves and finalizes the registration process.
 * 
 * @param {object} componentConfig 
 * @param {object} registrationData 
 * @param {function} setRegistrationData 
 * @param {function} saveSuccessCallback
 */
function finalizeRegistration(componentConfig, registrationData, setRegistrationData, saveSuccessCallback) {

    let userRegistrationId = null;
    let organizationName = null;
    let mpgInfo = null;
    const context = componentConfig.context;
    

    const validateApiResponse = (response) => {

        if (response.status !== 200 || !response.data) {
            throw response;
        }

    }

    const validateMpgApiResponse = (response) => {

        if (response.status !== 200 || !response.data) {
            registerCreateFailEvent(response.data.userRegistrationId);
            throw response;
        }
        
    }

    const handleSaveResponse = (response) => {

        validateApiResponse(response);

        userRegistrationId = response.data.userRegistrationId;
        organizationName = response.data.personalInformation.organizationName;

        if(typeof saveSuccessCallback === 'function') {
            saveSuccessCallback();
        }

        return {
            userRegistrationId: userRegistrationId,
            orgName: organizationName,
            recaptchaToken: registrationData.submittedRecaptchaToken
        };
    }

    const prepareNewCreditCardIfExists = (registrationInfo) => { 

        if (registrationData.metadata.isBillingRequired && registrationData.registrationModel?.billingInformation?.selectedPayWithNewCard) {
            return getMpgKey(registrationInfo)
                .then(handleMpgKeyResponse)
                .then(createMpgCreditCard)
                .then(handleMpgCreateResponse)
                .then(callManualMpgRedirect)
                .then(handleManualRedirectResponse);
        } else {
            return registrationInfo.userRegistrationId;
        }

    }

    const handleMpgKeyResponse = (response) => {

        validateApiResponse(response);

        mpgInfo = response.data;

        return {
            mpgInfo: mpgInfo,
            ccInfo: registrationData.creditCardInfo
        };
    }

    const handleMpgCreateResponse = (response) => {

        validateMpgApiResponse(response);

        const mpgCreateData = {
            url: mpgInfo.mpgReturnURL,
            data: {
                userRegistrationId: userRegistrationId,
                organizationName: organizationName,
                mpgKey: mpgInfo.key
            }
        };

        return mpgCreateData
    }

    const handleManualRedirectResponse = (response) => {

        validateApiResponse(response);

        return userRegistrationId;
    }

    const finalizeRegistration = (userRegistrationId) => {
        switch (context) {
            case 'ADD_CONTACT':
                return finalizeAddContact(userRegistrationId);
            case 'PUBLIC_REGISTRATION':
                return finalizeUserRegistration(userRegistrationId);
            default:
                throw {finalizeError: 'Unable to finalize registration. Unknown context provided.'};
        }
    }

    const handleFinalizeResponse = (response) => {

        validateApiResponse(response);

        if (doesRedirectToConfirmationPage(response.data.registrationStatus)) {
            handleDataLayerPushes(response.data);
        }

        const registrationDataCopy = Object.assign({}, registrationData);

        // Clear errors in case registrationData was not updated yet (state setting is asynchronous)
        registrationDataCopy.validationErrors = [];
        registrationDataCopy.metadata.status = response.data.registrationStatus;

        setRegistrationData(registrationDataCopy);

    }

    const handleDataLayerPushes = (data) => {
        pushRegistrationConfirmationVirtualPageView(context, data, registrationData);

        // Don't try to parse an empty string to avoid exceptions
        let eCommerceInfo = data.googleAnalyticsECommerceInfo ? JSON5.parse(data.googleAnalyticsECommerceInfo) : null;
        let eCommerceInfoEvents = data.googleAnalyticsECommerceInfoEvents ? JSON5.parse(data.googleAnalyticsECommerceInfoEvents) : null;
        let eCommerceUserData = data.googleAnalyticsUserData ? JSON5.parse(data.googleAnalyticsUserData) : null;

        pushRegistrationECommerceData(eCommerceInfo, eCommerceInfoEvents, eCommerceUserData, context, registrationData.metadata.isBillingRequired);

    }

    const handleCaughtError = (error) => {

        const errorArray = buildErrorArray(error);
        const registrationDataCopy = Object.assign({}, registrationData);

        registrationDataCopy.validationErrors = errorArray;
        registrationDataCopy.metadata.isSubmitting = false;
        registrationDataCopy.metadata.formSubmissionFailures++;

        setRegistrationData(registrationDataCopy);

        scrollToElem(".registration-frame");

    }

    const stripBillingInfoIfNotRequired = (registrationData) => {

        const payingWithNewCard = registrationData.registrationModel.billingInformation.selectedPayWithNewCard;
        const registrationModelCopy = Object.assign({}, registrationData.registrationModel);

        if (!registrationData.metadata.isBillingRequired || !payingWithNewCard) {
            registrationModelCopy.billingInformation = null;
        }

        return registrationModelCopy;
    }

    // We know there are no errors in the form, clear credit card values before we begin for PCI compliance...
    if (registrationData.metadata.isBillingRequired) {
        resetCreditCardFormValues(componentConfig);
    }

    const registrationModel = stripBillingInfoIfNotRequired(registrationData);


    return saveRegistrationModel(registrationModel, componentConfig, registrationData.metadata.currentStep)
        .then(handleSaveResponse)
        .then(prepareNewCreditCardIfExists)
        .then(finalizeRegistration)
        .then(handleFinalizeResponse)
        .catch(handleCaughtError)
        .finally(resetApplicableComponentsSubmittingState(componentConfig));

}

/**
 * Reset the credit card form field values. This should be done when the entire registration is 
 * successfully submitted. This needs to be done in order to be PCI compliant.
 * 
 * @param {object} componentConfig 
 */
const resetCreditCardFormValues = (componentConfig) => {

    const form = componentConfig.componentDefinition.BillingInformation.props.formikRef.current;

    form.setFieldValue('cardHolderName', '', false);
    form.setFieldValue('cardNumber', '', false);
    form.setFieldValue('expiryDate', '', false);
    form.setFieldValue('securityCode', '', false);

    form.setTouched({
        'cardHolderName': false,
        'cardNumber': false,
        'expiryDate': false,
        'securityCode': false
    }, false);

    form.setErrors({
        'cardHolderName': '',
        'cardNumber': '',
        'expiryDate': '',
        'securityCode': ''
    });

}

/**
 * Saves the registration data.
 * 
 * @param {object} componentConfig 
 * @param {object} registrationData 
 * @param {function} setRegistrationData 
 * @param {function} successCallback 
 */
const saveRegistration = (componentConfig, registrationData, setRegistrationData, successCallback) => {

    // Call server to save the model.
    return saveRegistrationModel(registrationData.registrationModel, componentConfig, registrationData.metadata.currentStep).then((response) => {
        
        // If action was successful...
        if (response.status === 200 && response.data) {

            const registrationDataCopy = Object.assign({}, registrationData);

            // Make sure there are no validation errors
            registrationDataCopy.validationErrors = [];

            // Update subscription model with latest from server.
            registrationDataCopy.registrationModel.userRegistrationId = response.data.userRegistrationId;

            // For each active section component, need to see if we need to update the matching model key.
            componentConfig.activeComponents.forEach((componentName) => {

                const componentDef = componentConfig.componentDefinition[componentName];

                // Update the registrationModel of each section if the server's response contains a matching key.
                if (response.data[componentDef.props.modelKey]) {
                    registrationDataCopy.registrationModel[componentDef.props.modelKey] = response.data[componentDef.props.modelKey];
                }
                
            });

            // Invoke the successCallback if it is defined.
            if (typeof successCallback === 'function') {
                successCallback(componentConfig, registrationDataCopy);
            }

            // If we're not already on the last step, increment currentStep and scroll to next section.
            if (registrationDataCopy.metadata.currentStep < componentConfig.activeComponents.length) {
                ++registrationDataCopy.metadata.currentStep;
                scrollToElem(".section-" + (registrationDataCopy.metadata.currentStep));
            }

            // Update state.
            setRegistrationData(registrationDataCopy);
            
        } else {
            // If an error occurred, throw response, will be handled in subsequent catch...
            throw response;
        }

    }).catch((error) => {

        if (error.response) {

            const registrationDataCopy = Object.assign({}, registrationData);

            registrationDataCopy.validationErrors = error.response.data.validationErrors;

            setRegistrationData(registrationDataCopy);

            // Scroll to top of page when there are errors
            scrollToElem(".registration-frame");

        } else {
            console.error(error);
        }

    }).finally(resetApplicableComponentsSubmittingState(componentConfig));

};
